import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth, SubscriptionPlan } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Check, CreditCard, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedPlan?: SubscriptionPlan;
  billingPeriod?: "monthly" | "quarterly" | "annual";
  affiliateCode?: string | null;
}

interface PlanDetails {
  name: string;
  price: number;
  features: string[];
  highlighted?: boolean;
}

type BillingPeriod = "monthly" | "quarterly" | "annual";

interface BillingPriceModifier {
  label: string;
  months: number;
  discount: number;
}

// Detalhes dos planos disponíveis com preços corretos
const planDetails: Record<SubscriptionPlan, PlanDetails> = {
  free: {
    name: "Gratuito",
    price: 0,
    features: ["Acesso a 2 palpites de Milhar", "Acesso a 2 palpites de Centena"]
  },
  basic: {
    name: "Palpite Guru Mensal",
    price: 34.90,
    features: [
      "Acesso completo a todos os palpites", 
      "10 palpites de Milhar por dia", 
      "10 palpites de Centena por dia", 
      "4 palpites de Grupo por dia",
      "10 palpites de Duque e Terno por dia"
    ],
    highlighted: false
  },
  premium: {
    name: "Palpite Guru Trimestral",
    price: 54.90,
    features: [
      "Todos os recursos do Plano Mensal", 
      "Economia de R$ 20,00 por trimestre", 
      "Acesso ao Software Turbinado", 
      "10 palpites de Milhar e Centena por dia",
      "4 palpites de Grupo por dia"
    ],
    highlighted: true
  },
  vip: {
    name: "Palpite Guru Anual",
    price: 174.90,
    features: [
      "Todos os recursos dos outros planos", 
      "Maior economia: R$ 124,00 por ano", 
      "Acesso ao Software Turbinado", 
      "Acesso completo e ilimitado",
      "Suporte prioritário"
    ],
    highlighted: false
  }
};

const billingOptions: Record<BillingPeriod, BillingPriceModifier> = {
  monthly: {
    label: "Mensal",
    months: 1,
    discount: 0
  },
  quarterly: {
    label: "Trimestral",
    months: 3,
    discount: 0
  },
  annual: {
    label: "Anual",
    months: 12,
    discount: 0
  }
};

const PaymentModal: React.FC<PaymentModalProps> = ({ 
  open, 
  onClose, 
  selectedPlan = "premium",
  billingPeriod: initialBillingPeriod = "monthly",
  affiliateCode 
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(initialBillingPeriod);

  const nubankLinks: Record<string, string> = {
    basic: "https://payment-link-v3.ton.com.br/pl_ZQwzvp2lJo7NmVQHDxSzXV5YLBM6rP0A",
    premium: "https://payment-link-v3.ton.com.br/pl_MOmQDl0pqL34xMDFQOfyRWdVvkN1aYZg",
    vip: "https://payment-link-v3.ton.com.br/pl_L70p9dexNmVXEJeJTLU3AgoaMrlk5Zb2",
    trial15: "https://payment-link-v3.ton.com.br/pl_jmE3QdVoaOkNQxyT0qFpPWqpbYzBAxXw",
  };

  const handlePayment = async () => {
    if (!user) {
      toast({
        title: "Erro de autenticação",
        description: "Você precisa estar logado para fazer uma assinatura.",
        variant: "destructive",
      });
      return;
    }
    
    const link = nubankLinks[selectedPlan];
    if (!link) {
      toast({
        title: "Plano não disponível",
        description: "Este plano não possui link de pagamento configurado.",
        variant: "destructive",
      });
      return;
    }

    console.log("Abrindo checkout Nubank para:", { selectedPlan, billingPeriod, link });
    window.open(link, '_blank');
    onClose();
  };

  // Calcular preço baseado no plano e período
  const calculatePrice = () => {
    const basePrice = planDetails[selectedPlan].price;
    const { months } = billingOptions[billingPeriod];
    
    // Para cada plano, usar o preço específico
    let totalPrice = basePrice;
    if (selectedPlan === "basic" && billingPeriod === "monthly") {
      totalPrice = 34.90;
    } else if (selectedPlan === "premium" && billingPeriod === "quarterly") {
      totalPrice = 54.90;
    } else if (selectedPlan === "vip" && billingPeriod === "annual") {
      totalPrice = 174.90;
    }
    
    return {
      monthlyPrice: basePrice,
      totalPrice: totalPrice
    };
  };

  // Formatar preço para moeda brasileira
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const priceDetails = calculatePrice();

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md md:max-w-xl">
        <DialogHeader>
          <DialogTitle>Assinar {planDetails[selectedPlan].name}</DialogTitle>
          <DialogDescription>
            Escolha como deseja pagar pelo plano {planDetails[selectedPlan].name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Card className="p-4 border border-primary/20 bg-primary/5">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">{planDetails[selectedPlan].name}</h3>
                <p className="text-sm text-muted-foreground">
                  Cancele a qualquer momento
                </p>
              </div>
              <div className="text-2xl font-bold">
                {formatCurrency(priceDetails.totalPrice)}
                <span className="text-sm font-normal text-muted-foreground">
                  /{billingPeriod === "monthly" ? "mês" : billingPeriod === "quarterly" ? "trimestre" : "ano"}
                </span>
              </div>
            </div>
            
            <div className="space-y-1">
              {planDetails[selectedPlan].features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <Check size={16} className="mr-2 mt-0.5 text-green-500" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </Card>
          
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-medium mb-2">Detalhes do pagamento</h4>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Plano</span>
                <span>{planDetails[selectedPlan].name}</span>
              </div>
              
              <div className="flex justify-between font-medium pt-2 border-t border-border/30 mt-2">
                <span>Total</span>
                <span>{formatCurrency(priceDetails.totalPrice)}</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isProcessing}
            className="sm:w-auto w-full"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handlePayment} 
            disabled={isProcessing}
            className="sm:w-auto w-full"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Pagar {formatCurrency(priceDetails.totalPrice)}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
